import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import UserNavbar from '../components/UserNavbar';
import FilterSelect from '../components/FilterSelect';
import { toast } from 'react-toastify';

const SPECIALIZATION_OPTIONS = [
  { value: '', label: 'All Specializations' },
  { value: 'Cardiologist', label: 'Cardiologist' },
  { value: 'Dermatologist', label: 'Dermatologist' },
  { value: 'Pediatrician', label: 'Pediatrician' },
  { value: 'Orthopedic', label: 'Orthopedic' },
  { value: 'Neurologist', label: 'Neurologist' },
  { value: 'General Physician', label: 'General Physician' },
];

const RATING_OPTIONS = [
  { value: '', label: 'Any' },
  { value: '4', label: '4★ & above' },
  { value: '3', label: '3★ & above' },
  { value: '2', label: '2★ & above' },
];

const SORT_OPTIONS = [
  { value: 'rating', label: 'Highest Rated' },
  { value: 'experience', label: 'Most Experienced' },
];

function DoctorList() {
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [updatingFavorites, setUpdatingFavorites] = useState(new Set());
  const [doctorAvailability, setDoctorAvailability] = useState({}); // { doctorId: boolean }

  // 🔹 FILTER STATES
  const [filters, setFilters] = useState({
    location: '',
    specialization: ''
  });
  const [ratingFilter, setRatingFilter] = useState('');
  const [sortBy, setSortBy] = useState('rating'); // 'rating' or 'experience'

  useEffect(() => {
    fetchData();
  }, [filters, ratingFilter]);

  const fetchData = async () => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.location && filters.location.trim()) {
        queryParams.append('location', filters.location.trim());
      }
      if (filters.specialization && filters.specialization.trim()) {
        queryParams.append('specialization', filters.specialization.trim());
      }

      let docRes;
      if (ratingFilter) {
        // Use the new ratings endpoint if needed, but since we joined average_rating to Doctor model,
        // we can just use the standard list and filter on the frontend or backend.
        // For now, let's keep it simple and update the query or filter here.
        queryParams.append('min_rating', ratingFilter);
      }

      docRes = await api.get(`user/doctor_list/?${queryParams.toString()}`);
      const favRes = await api.get('user/favorites/');

      // No need to log - backend is returning full URLs

      setDoctors(docRes.data);
      setFavorites(favRes.data);

      // Fetch availability for all doctors in parallel
      fetchDoctorAvailability(docRes.data);
    } catch (err) {
      console.error("Error fetching doctor list:", err);
      if (err.response?.status === 401) navigate('/user/login');
    } finally {
      setLoading(false);
    }
  };

  // Check availability for each doctor by fetching their available slots
  const fetchDoctorAvailability = async (doctorsList) => {
    try {
      // Fetch slots for all doctors in parallel
      const availabilityPromises = doctorsList.map(async (doc) => {
        try {
          if (!doc.id) {
            console.warn("Doctor object missing ID:", doc);
            return { doctorId: undefined, isAvailable: false };
          }
          const slotsRes = await api.get(`appointments/user/slots/${doc.id}/`);
          // Doctor is available if there's at least one slot
          return { doctorId: doc.id, isAvailable: slotsRes.data.length > 0 };
        } catch (err) {
          // If error (e.g., 401, 404), consider doctor unavailable
          console.error(`Error fetching slots for doctor ${doc.id}:`, err);
          return { doctorId: doc.id, isAvailable: false };
        }
      });

      const availabilityResults = await Promise.all(availabilityPromises);
      const availabilityMap = {};
      availabilityResults.forEach(({ doctorId, isAvailable }) => {
        availabilityMap[doctorId] = isAvailable;
      });
      setDoctorAvailability(availabilityMap);
    } catch (err) {
      console.error("Error fetching doctor availability:", err);
    }
  };

  // Check if doctor.id exists in user's favorite doctors list
  const isFavorite = (doctorId) => {
    return favorites.some(f => f?.doctor_id === doctorId);
  };

  // Toggle favorite with optimistic UI update
  const toggleFavorite = async (doctorId) => {
    const wasFavorite = isFavorite(doctorId);

    // Optimistic UI update - instant feedback
    if (wasFavorite) {
      setFavorites(prev => prev.filter(f => f?.doctor_id !== doctorId));
    } else {
      setFavorites(prev => [...prev, { doctor_id: doctorId }]);
    }

    setUpdatingFavorites(prev => new Set(prev).add(doctorId));

    try {
      if (wasFavorite) {
        const response = await api.post('user/favorites/remove/', { doctor_id: doctorId });
        // Use response data if available, otherwise refresh
        if (response.data?.favorites) {
          setFavorites(response.data.favorites);
        } else {
          const res = await api.get('user/favorites/');
          setFavorites(res.data);
        }
      } else {
        const response = await api.post('user/favorites/add/', { doctor_id: doctorId });
        // Use response data if available, otherwise refresh
        if (response.data?.favorites) {
          setFavorites(response.data.favorites);
        } else {
          const res = await api.get('user/favorites/');
          setFavorites(res.data);
        }
      }
    } catch (err) {
      // Revert on error - try to refresh from backend
      console.error("Failed to update favorites:", err);
      console.error("Error response:", err.response);
      console.error("Error status:", err.response?.status);
      console.error("Error data:", err.response?.data);
      console.error("Error message:", err.message);

      // Show specific error message
      const errorMessage = err.response?.data?.error ||
        err.response?.data?.msg ||
        err.response?.data?.message ||
        err.message ||
        "Failed to update favorites. Please try again.";

      // If it's a 401, redirect to login
      if (err.response?.status === 401) {
        navigate('/user/login');
        return;
      }

      try {
        // Try to refresh favorites from backend
        const res = await api.get('user/favorites/');
        setFavorites(res.data);
      } catch (refreshErr) {
        // If refresh also fails, just log it
        console.error("Failed to refresh favorites:", refreshErr);
        // If it's a 401, redirect to login
        if (refreshErr.response?.status === 401) {
          navigate('/user/login');
          return;
        }
      }

      // Only show alert if it's not a 404 (doctor not found in favorites is okay)
      if (err.response?.status !== 404) {
        toast.error(`Error: ${errorMessage}\nStatus: ${err.response?.status || 'Unknown'}`, {
          position: "bottom-right",
          autoClose: 3000,
        });
      }
    } finally {
      setUpdatingFavorites(prev => {
        const newSet = new Set(prev);
        newSet.delete(doctorId);
        return newSet;
      });
    }
  };

  // Helper function to get image URL
  const getImageUrl = (image) => {
    // Handle null, undefined, or empty string
    if (!image || (typeof image === 'string' && image.trim() === '')) {
      return "https://via.placeholder.com/400x250?text=Medical+Image";
    }

    // If already a full URL (from backend), return as is
    if (image.startsWith('http://') || image.startsWith('https://')) {
      return image;
    }

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    // If starts with /media/, just prepend API URL
    if (image.startsWith('/media/')) {
      return `${apiUrl}${image}`;
    }

    // Add /media/ prefix for paths like "doctor_images/filename.png"
    return `${apiUrl}/media/${image}`;
  };

  // Filter and sort doctors: Available first, then unavailable, then loading
  const filteredDoctors = doctors
    .filter(doc =>
      doc.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      // Primary sort by availability (Available first)
      const aAvailable = doctorAvailability[a.id];
      const bAvailable = doctorAvailability[b.id];
      if (aAvailable === true && bAvailable !== true) return -1;
      if (bAvailable === true && aAvailable !== true) return 1;

      // Secondary sort based on user preference
      if (sortBy === 'rating') {
        return (b.average_rating || 0) - (a.average_rating || 0);
      } else if (sortBy === 'experience') {
        const expA = parseInt(a.experience) || 0;
        const expB = parseInt(b.experience) || 0;
        return expB - expA;
      }
      return 0;
    });

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-light min-vh-100">
      <UserNavbar />

      <div className="container py-5">
        <div className="row mb-5 align-items-center">
          <div className="col-lg-6">
            <h2 className="fw-bold mb-1">Find Your Specialist</h2>
            <p className="text-muted">Browse through our network of certified medical professionals.</p>
          </div>
          <div className="col-lg-6">
            <div className="input-group input-group-lg shadow-sm rounded-4 overflow-hidden border-0">
              <span className="input-group-text bg-white border-0 text-primary">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control border-0 px-2"
                placeholder="Search by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* 🔹 FILTERS SECTION */}
        <div className="card border-0 shadow-sm rounded-4 p-3 p-md-4 mb-5 overflow-visible">
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <label className="form-label small fw-bold text-muted">Location</label>
              <div className="input-group">
                <span className="input-group-text bg-light border-0"><i className="bi bi-geo-alt"></i></span>
                <input
                  type="text"
                  className="form-control bg-light border-0"
                  placeholder="Filter by city/area..."
                  value={filters.location}
                  onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                />
              </div>
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label small fw-bold text-muted">Specialization</label>
              <FilterSelect
                value={filters.specialization}
                onChange={(specialization) => setFilters({ ...filters, specialization })}
                options={SPECIALIZATION_OPTIONS}
                placeholder="All Specializations"
                aria-label="Specialization"
              />
            </div>
            <div className="col-12 col-sm-6 col-md-3">
              <label className="form-label small fw-bold text-muted">Minimum Rating</label>
              <FilterSelect
                value={ratingFilter}
                onChange={setRatingFilter}
                options={RATING_OPTIONS}
                placeholder="Any"
                aria-label="Minimum rating"
              />
            </div>
            <div className="col-12 col-sm-6 col-md-3">
              <label className="form-label small fw-bold text-muted">Sort By</label>
              <FilterSelect
                value={sortBy}
                onChange={setSortBy}
                options={SORT_OPTIONS}
                placeholder="Sort"
                aria-label="Sort by"
              />
            </div>
            <div className="col-12 col-md-2 d-flex align-items-end">
              <button
                className="btn btn-outline-secondary w-100 fw-bold"
                onClick={() => {
                  setFilters({ location: '', specialization: '' });
                  setRatingFilter('');
                  setSortBy('rating');
                }}
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        <div className="row g-4">
          {filteredDoctors.length === 0 ? (
            <div className="col-12 text-center py-5">
              <i className="bi bi-search fs-1 text-muted mb-3 d-block"></i>
              <h4>No doctors found matching "{searchQuery}"</h4>
              <button className="btn btn-link" onClick={() => setSearchQuery('')}>Clear search</button>
            </div>
          ) : (
            filteredDoctors.map(doc => {
              const favorited = isFavorite(doc.id);
              const isUpdating = updatingFavorites.has(doc.id);
              const isAvailable = doctorAvailability[doc.id] !== undefined
                ? doctorAvailability[doc.id]
                : null; // null means still loading

              return (
                <div key={doc.id} className="col-md-6 col-lg-4 col-xl-3">
                  <div className="card border-0 shadow-sm rounded-4 h-100 overflow-hidden hover-shadow transition">
                    <div className="position-relative">
                      <img
                        src={getImageUrl(doc.image)}
                        alt={doc.name}
                        className="card-img-top object-fit-cover"
                        style={{ height: '220px', width: '100%' }}
                        onError={(e) => {
                          // Only show error if it's not already the placeholder
                          if (!e.target.src.includes('placeholder')) {
                            console.error(`Image failed to load for ${doc.name}:`, doc.image);
                            console.error('Attempted URL:', e.target.src);
                          }
                          e.target.src = "https://via.placeholder.com/400x250?text=Medical+Image";
                        }}
                      />

                      {/* Heart Icon: Red filled (❤️) if favorited, Normal outline if not */}
                      <button
                        className={`btn position-absolute top-0 end-0 m-2 border-0 ${isUpdating ? 'opacity-50' : ''}`}
                        style={{
                          background: 'transparent',
                          padding: '0',
                          zIndex: 10,
                          boxShadow: 'none'
                        }}
                        onClick={() => toggleFavorite(doc.id)}
                        disabled={isUpdating}
                        aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
                        title={favorited ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        <i className={`bi ${favorited ? 'bi-heart-fill text-danger' : 'bi-heart text-white'} fs-4`} ></i>
                      </button>
                      {/* Availability Badge - Top Left */}
                      {isAvailable !== null && (
                        <span className={`position-absolute top-0 start-0 m-2 badge rounded-pill px-2 py-1 small ${isAvailable ? 'bg-success' : 'bg-secondary'
                          }`} style={{ fontSize: '0.7rem' }}>
                          <i className={`bi ${isAvailable ? 'bi-check-circle' : 'bi-x-circle'} me-1`}></i>
                          {isAvailable ? 'Available' : 'Unavailable'}
                        </span>
                      )}
                      {/* Experience Badge - Bottom Left */}
                      {doc.experience && (
                        <span className="position-absolute bottom-0 start-0 m-3 badge bg-primary rounded-pill px-3 py-2">
                          {doc.experience} Years Exp.
                        </span>
                      )}
                    </div>
                    <div className="card-body p-4">
                      <h5 className="fw-bold mb-1"> {doc.name}</h5>
                      <p className="text-primary small fw-bold text-uppercase mb-2">
                        {doc.specialization || 'General Physician'}
                      </p>

                      {/* RATING DISPLAY */}
                      <div className="mb-2 d-flex align-items-center">
                        <div className="text-warning me-2">
                          {/* Show filled stars based on rating */}
                          {[...Array(5)].map((_, i) => (
                            <i
                              key={i}
                              className={`bi ${i < Math.floor(doc.average_rating || 0) ? 'bi-star-fill' : (i < (doc.average_rating || 0) ? 'bi-star-half' : 'bi-star')} me-1`}
                            ></i>
                          ))}
                        </div>
                        {/* <span className="fw-bold text-dark me-1">{doc.average_rating || '0.0'}</span>
                        <span className="text-muted small">({doc.total_reviews || 0} reviews)</span> */}
                      </div>
                      {/* Availability Status in Card Body */}
                      {isAvailable !== null && (
                        <div className="mb-2">
                          <span className={`badge ${isAvailable ? 'bg-success bg-opacity-10 text-success' : 'bg-secondary bg-opacity-10 text-secondary'} rounded-pill px-3 py-1 small`}>
                            <i className={`bi ${isAvailable ? 'bi-check-circle' : 'bi-x-circle'} me-1`}></i>
                            {isAvailable ? 'Available Now' : 'Currently Unavailable'}
                          </span>
                        </div>
                      )}

                      <div className="d-flex align-items-center mb-3 text-muted small">
                        <i className="bi bi-geo-alt me-2 text-secondary"></i>
                        <span className="text-truncate">{doc.address || 'Online Consultation'}</span>
                      </div>

                      <div className="d-grid gap-2">
                        <button
                          className="btn btn-primary rounded-3 fw-bold py-2"
                          onClick={() => navigate(`/user/book-appointment/${doc.id}`)}
                          disabled={isAvailable === false}
                        >
                          Book Appointment
                        </button>
                        <button
                          className="btn btn-outline-light text-dark border rounded-3 fw-medium py-2"
                          onClick={() => navigate(`/user/doctor-profile/${doc.id}`)}
                        >
                          View Profile
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <style>{`
        .hover-shadow:hover {
          transform: translateY(-5px);
          box-shadow: 0 1rem 3rem rgba(0,0,0,.1) !important;
        }
        .transition {
          transition: all 0.3s ease;
        }
        @media (max-width: 768px) {
          .card-img-top {
            height: 200px !important;
          }
        }
      `}</style>
    </div>
  );
}

export default DoctorList;
